import { useRef, useState, useCallback, useEffect } from "react";

// Makes a floating helper draggable along its screen edge — UP/DOWN and, when
// enabled, LEFT/RIGHT (it snaps to whichever edge you release nearest). Works
// with mouse + touch (Pointer Events). Vertical position gently SNAPS to a tidy
// grid so icons line up. Position + side persist per-FAB. A real drag "swallows"
// the trailing click so the FAB action doesn't fire.
export function useDraggableFab(storageKey, defaultBottom, opts = {}) {
  const allowSide = opts.side !== false;
  const snapStep = opts.snap || 24; // gentle vertical snap grid (px)
  const edge = opts.edge != null ? opts.edge : 12;

  const readBottom = () => {
    try { const v = parseInt(localStorage.getItem(storageKey), 10); return Number.isFinite(v) ? v : defaultBottom; } catch { return defaultBottom; }
  };
  const readSide = () => {
    try { const s = localStorage.getItem(`${storageKey}_side`); return s === "left" || s === "right" ? s : (opts.defaultSide || "right"); } catch { return opts.defaultSide || "right"; }
  };

  const [bottom, setBottom] = useState(readBottom);
  const [side, setSide] = useState(readSide);
  // Follow a route-aware default when the user hasn't manually dragged this FAB.
  // (These FABs mount once at the app root, so we react to defaultBottom changes.)
  useEffect(() => {
    try { if (localStorage.getItem(storageKey) == null) setBottom(defaultBottom); }
    catch { setBottom(defaultBottom); }
  }, [defaultBottom, storageKey]);
  // "Tidy up" handle: snap this FAB back to its default stacked slot and forget
  // any dragged position (broadcast by the FabTidyToggle).
  useEffect(() => {
    const onTidy = () => {
      try { localStorage.removeItem(storageKey); localStorage.removeItem(`${storageKey}_side`); } catch { /* noop */ }
      setBottom(defaultBottom);
      setSide(opts.defaultSide || "right");
    };
    window.addEventListener("iw-fabs-tidy", onTidy);
    return () => window.removeEventListener("iw-fabs-tidy", onTidy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, defaultBottom, opts.defaultSide]);
  const [drag, setDrag] = useState(null); // {x} while freely dragging, else null
  const d = useRef({ down: false, moved: false, captured: false, startX: 0, startY: 0, startBottom: 0, lastBottom: defaultBottom, lastX: 0, swallow: false });

  const clampB = (b) => Math.max(72, Math.min((typeof window !== "undefined" ? window.innerHeight : 800) - 96, b));
  const snap = (b) => Math.round(b / snapStep) * snapStep;

  const onPointerDown = useCallback((e) => {
    d.current.down = true;
    d.current.moved = false;
    d.current.captured = false;
    d.current.startX = e.clientX;
    d.current.startY = e.clientY;
    d.current.startBottom = bottom;
    d.current.lastBottom = bottom;
    d.current.lastX = e.clientX;
  }, [bottom]);

  const onPointerMove = useCallback((e) => {
    if (!d.current.down) return;
    const dy = d.current.startY - e.clientY;
    const dx = e.clientX - d.current.startX;
    if (Math.abs(dy) > 10 || Math.abs(dx) > 10) d.current.moved = true;
    if (d.current.moved) {
      if (!d.current.captured) {
        try { e.currentTarget.setPointerCapture(e.pointerId); d.current.captured = true; } catch { /* noop */ }
      }
      const nb = clampB(d.current.startBottom + dy);
      d.current.lastBottom = nb;
      d.current.lastX = e.clientX;
      setBottom(nb);
      if (allowSide) setDrag({ x: e.clientX });
    }
  }, [allowSide]);

  const endDrag = useCallback((e) => {
    if (!d.current.down) return;
    d.current.down = false;
    if (d.current.moved) {
      const snapped = clampB(snap(d.current.lastBottom));
      setBottom(snapped);
      let nextSide = side;
      if (allowSide) {
        const w = typeof window !== "undefined" ? window.innerWidth : 400;
        nextSide = d.current.lastX < w / 2 ? "left" : "right";
        setSide(nextSide);
      }
      try {
        localStorage.setItem(storageKey, String(snapped));
        if (allowSide) localStorage.setItem(`${storageKey}_side`, nextSide);
      } catch { /* quota */ }
      d.current.swallow = true;
      setTimeout(() => { d.current.swallow = false; }, 60);
      // A real drag can leave the finger over a DIFFERENT element on release;
      // the browser would then fire a stray "click" on whatever is underneath
      // (e.g. a CTA that navigates to the subscription page). Swallow that one
      // click globally so dragging a FAB never triggers the page beneath it.
      try {
        const suppress = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener("click", suppress, { capture: true, once: true });
        setTimeout(() => { try { document.removeEventListener("click", suppress, { capture: true }); } catch { /* noop */ } }, 350);
      } catch { /* noop */ }
    }
    setDrag(null);
    if (d.current.captured) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
      d.current.captured = false;
    }
  }, [storageKey, side, allowSide, snapStep]); // eslint-disable-line

  const bind = { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag };
  const shouldSwallowClick = useCallback(() => d.current.swallow, []);

  // Ready-to-apply positioning style (fixed left/right + bottom). While actively
  // dragging horizontally it follows the finger; on release it snaps to an edge.
  const w = typeof window !== "undefined" ? window.innerWidth : 400;
  const style = drag && allowSide
    ? { bottom, left: Math.max(4, Math.min(w - 52, drag.x - 24)), right: "auto", touchAction: "none" }
    : { bottom, [side]: edge, [side === "right" ? "left" : "right"]: "auto", touchAction: "none" };

  return { bottom, side, style, bind, shouldSwallowClick };
}
